import _ from 'lodash';
import { DateTime } from 'luxon';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import { isServiceChargeItem } from '~Utils/serviceCharge';
import { LocalItem, Station, TicketMessage } from '~Types';

const getItemCourseId = (item: LocalItem) => item.type ?? item.courseType?.id ?? null;
import { StorageData } from '~Components/Storage';
import { Printer } from '~reducers';
import { TicketOrderInfo } from '.';

interface KDSProcessingParams {
  items: LocalItem[];
  stations: Station[];
  orderInfo: TicketOrderInfo;
  printers: Printer[];
  storage: StorageData;
  atleastOneDeviceHasKDS: boolean;
  useCoursesInKDS: boolean;
  onlyPhysicalBongs?: boolean;
  orderStatusApi: any;
  orderStatus: any;
  logger: any;
  // Messages added to the ticket (e.g. "Wait <course>"). Used to hold a course
  // in the KDS instead of firing it immediately.
  ticketMessages?: TicketMessage[];
}

export interface KDSProcessingResult {
  orderId: string | undefined;
  orderIdsByCourse: { [courseId: string]: string };
  itemsToAdd: LocalItem[];
  homelessItems: LocalItem[];
}

// Helper function to sleep
async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function processKDSOrder({
  items,
  stations,
  orderInfo,
  printers,
  storage,
  atleastOneDeviceHasKDS,
  useCoursesInKDS,
  onlyPhysicalBongs,
  orderStatusApi,
  orderStatus,
  logger,
  ticketMessages,
}: KDSProcessingParams): Promise<KDSProcessingResult> {
  logger.info('[KDS] Processing KDS order', {
    items,
    stations,
    orderInfo,
    printers,
    storage,
    atleastOneDeviceHasKDS,
    onlyPhysicalBongs,
  });

  // Get all station items and categories for KDS logic
  const allStationItems = _.flatMap(stations, (station) => station.items);
  const allStationCategories = _.flatMap(stations, (station) => station.categories);

  // Partition items based on if they belong to a station or not for KDS
  const printerStationsAreUsed =
    printers.some((printer) => printer.stations.length > 0) ||
    (atleastOneDeviceHasKDS && stations.length > 0);
  const isAppOrder = !!orderInfo.realId;

  const [itemsToAdd, homelessItems] =
    !printerStationsAreUsed || (isAppOrder && !storage.enableNewKDSAppOrders)
      ? [items, []]
      : // Otherwise, filter for relevant items
        _.partition(items, (item) => {
          return (
            allStationItems.some((stItem) => stItem === item.itemId) ||
            allStationCategories.some(
              (stCat) => stCat.id === item.categoryId || stCat.name === item.categoryId,
            )
          );
        });

  logger.info('[KDS] Partitioned items', {
    itemsToAdd,
    homelessItems,
  });

  // These items won't be added to the KDS, logged for debug purposes
  if (homelessItems.length > 0 && isAppOrder) {
    logger.warn('Skipped items for app order ', {
      items: homelessItems,
      orderRealId: orderInfo.realId,
    });
  }

  // Store KDS orderId and pass it along to any eventual physical bong prints
  let orderId: string | undefined;
  const orderIdsByCourse: { [courseId: string]: string } = {};

  logger.info('[KDS] Adding items to KDS', {
    atleastOneDeviceHasKDS,
    itemsToAdd,
    onlyPhysicalBongs,
  });

  // If we use KDS and have bongable items, add bong to KDS.
  if (atleastOneDeviceHasKDS && itemsToAdd.length > 0 && !onlyPhysicalBongs) {
    logger.info("[KDS] We're using KDS and have bongable items, add bong to KDS");

    const nonServiceChargeItems = itemsToAdd.filter((item) => !isServiceChargeItem(item.title));

    if (useCoursesInKDS) {
      const groupedByCourse = _.groupBy(
        nonServiceChargeItems,
        (item) => getItemCourseId(item) || 'NO_COURSE',
      );
      const sortedCourseKeys = Object.keys(groupedByCourse).sort((a, b) => {
        const aOrder = groupedByCourse[a][0]?.courseType?.order ?? Number.MAX_SAFE_INTEGER;
        const bOrder = groupedByCourse[b][0]?.courseType?.order ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });

      const orderHasComment = orderInfo.comment && orderInfo.comment.length > 0;

      for (const courseKey of sortedCourseKeys) {
        const index = sortedCourseKeys.indexOf(courseKey);
        const courseItems = groupedByCourse[courseKey];

        if (!courseItems || courseItems.length === 0) {
          continue;
        }

        const courseId = courseKey === 'NO_COURSE' ? null : courseKey;

        // Late additions merge into the table's existing order for the same
        // course only while it hasn't been fired — once a course is fired the
        // kitchen is working on it, so later sends create a new bong. Sends
        // with an order comment still create their own bong.
        const existingOrderForCourse = !orderHasComment
          ? orderStatus.orders.find(
              (order: any) =>
                order.tableId &&
                order.tableId === orderInfo.tableId &&
                (order.courseId ?? null) === courseId &&
                order.fired === false &&
                order.status === ORDER_STATUS.ACCEPTED,
            )
          : undefined;

        let orderId: string;

        if (existingOrderForCourse) {
          logger.info("[KDS] Order existing for table and course, don't create new order", {
            orderId: existingOrderForCourse.id,
            courseId,
          });
          orderId = existingOrderForCourse.id;
        } else {
          // If staff pressed "Wait <course>" for this course, it adds a ticket
          // message with id `wait<courseId>`. When present, hold the course in the
          // KDS (create it as upcoming) instead of firing it immediately.
          const courseIsHeld =
            courseId !== null && ticketMessages?.some((m) => m.id === 'wait' + courseId);

          const shouldBeFired = storage.holdFirstCourse || courseIsHeld ? false : index === 0;

          orderId = orderStatusApi.addOrder(
            orderInfo.realId || '',
            orderInfo.comment || '',
            ORDER_STATUS.ACCEPTED,
            orderInfo.serviceType,
            orderInfo.tableName || orderInfo.orderNumber,
            orderInfo.tableId,
            orderInfo.origin,
            orderInfo.desiredTime,
            courseId,
            shouldBeFired,
          );

          await sleep(2);
        }

        orderIdsByCourse[courseKey] = orderId;

        logger.info('[KDS] Add course items to KDS', {
          orderId: orderId,
          courseId,
          itemCount: courseItems.length,
        });

        courseItems.forEach((item) => {
          logger.info('[KDS] Adding item to KDS');
          orderStatusApi.addItem(
            item.itemId,
            item.categoryId,
            orderId,
            item.message || '',
            item.variations.map((v) => v.id),
            item.removedVariations.map((v) => v.id),
            (item.defaultOrderStatus as ORDER_STATUS) || ORDER_STATUS.ACCEPTED,
            item.id,
            item.parentId,
            getItemCourseId(item) || undefined,
          );
        });
      }
    } else {
      // See if there is an order for this table already, that is collecting orders.
      // Late additions merge into the table's existing order if it's still ACCEPTED
      // and was created within the last 180 seconds. The age cap matters because
      // some venues disable order status progression on the KDS entirely, in which
      // case a table's order would otherwise sit ACCEPTED forever and every later
      // send for that table would keep merging into it, inheriting its original
      // (long stale) createdAt and rendering as instantly overdue.
      const existingOrderForTable = orderStatus.orders.find(
        (order: any) =>
          order.tableId &&
          order.tableId === orderInfo.tableId &&
          order.status === ORDER_STATUS.ACCEPTED &&
          DateTime.now() < DateTime.fromISO(order.createdAt).plus({ seconds: 180 }),
      );

      const orderHasComment = orderInfo.comment && orderInfo.comment.length > 0;

      if (existingOrderForTable && !orderHasComment) {
        logger.info("[KDS] Order existing for table, don't create new order");
        orderId = existingOrderForTable.id;
      } else {
        logger.info('[KDS] No order existing for table, create new order');
        orderId = orderStatusApi.addOrder(
          orderInfo.realId || '',
          orderInfo.comment || '',
          ORDER_STATUS.ACCEPTED,
          orderInfo.serviceType,
          orderInfo.tableName || orderInfo.orderNumber,
          orderInfo.tableId,
          orderInfo.origin,
          orderInfo.desiredTime,
        );
      }

      logger.info('[KDS] Add items to KDS', {
        orderId,
        itemsToAdd: nonServiceChargeItems,
      });

      nonServiceChargeItems.forEach((item) => {
        logger.info('[KDS] Adding item to KDS');
        orderStatusApi.addItem(
          item.itemId,
          item.categoryId,
          orderId!,
          item.message || '',
          item.variations.map((v) => v.id),
          item.removedVariations.map((v) => v.id),
          (item.defaultOrderStatus as ORDER_STATUS) || ORDER_STATUS.ACCEPTED,
          item.id,
          item.parentId,
          getItemCourseId(item) || undefined,
        );
      });
    }
  }

  return {
    orderId,
    orderIdsByCourse,
    itemsToAdd,
    homelessItems,
  };
}
